<template>
  <b-form novalidate class="job-posting-form" @submit.prevent="onSave(false)">
    <b-alert :show="!!form.$errorMessage" variant="danger" class="mb-4">
      {{ form.$errorMessage }}
    </b-alert>

    <b-alert
      :show="isEdit && job.status === 'published' && job.applications_count > 0"
      variant="info"
      class="mb-4"
    >
      People have already applied. When you save a change they are notified that
      the job was updated, and they can withdraw if it is no longer for them.
    </b-alert>

    <job-form-section
      title="Job details"
      description="Give the role a clear title and describe the work, so tradies can quickly see whether it suits them."
    >
      <b-form-group label="Job title*" label-for="job-title">
        <b-form-input
          id="job-title"
          v-model="form.title"
          maxlength="255"
          placeholder="e.g. Qualified electrician"
          :state="form.$hasError('title') ? false : null"
        />
        <span v-if="form.$hasError('title')" class="input-error d-block">
          {{ errorText('title') }}
        </span>
      </b-form-group>

      <b-form-group label="Description*" label-for="job-description">
        <b-form-textarea
          id="job-description"
          v-model="form.description"
          rows="7"
          placeholder="Describe the work, requirements and what you offer."
          :state="form.$hasError('description') ? false : null"
        />
        <span v-if="form.$hasError('description')" class="input-error d-block">
          {{ errorText('description') }}
        </span>
      </b-form-group>

      <b-row>
        <b-col md="6">
          <b-form-group
            label="Employment type*"
            label-for="job-employment-type"
          >
            <b-form-select
              id="job-employment-type"
              v-model="form.employment_type"
              :options="employmentTypeOptions"
              :state="form.$hasError('employment_type') ? false : null"
            />
            <span
              v-if="form.$hasError('employment_type')"
              class="input-error d-block"
            >
              {{ errorText('employment_type') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Trade" label-for="job-trade">
            <b-form-select
              id="job-trade"
              v-model="form.trade_id"
              :options="tradeOptions"
              :state="form.$hasError('trade_id') ? false : null"
            />
            <span v-if="form.$hasError('trade_id')" class="input-error d-block">
              {{ errorText('trade_id') }}
            </span>
          </b-form-group>
        </b-col>
      </b-row>
    </job-form-section>

    <job-form-section
      title="Pay"
      description="Set the pay type and range. You can keep the amounts off the advertisement if you would rather discuss pay with applicants."
    >
      <b-row>
        <b-col md="4">
          <b-form-group label="Pay type" label-for="job-pay-type">
            <b-form-select
              id="job-pay-type"
              v-model="form.pay_type"
              :options="payTypeOptions"
              :state="form.$hasError('pay_type') ? false : null"
            />
            <span v-if="form.$hasError('pay_type')" class="input-error d-block">
              {{ errorText('pay_type') }}
            </span>
          </b-form-group>
        </b-col>
        <template v-if="showPayAmounts">
          <b-col md="4">
            <b-form-group label="Minimum ($)" label-for="job-pay-min">
              <b-form-input
                id="job-pay-min"
                v-model="form.pay_min"
                type="number"
                min="0"
                step="0.01"
                :state="form.$hasError('pay_min') ? false : null"
              />
              <span
                v-if="form.$hasError('pay_min')"
                class="input-error d-block"
              >
                {{ errorText('pay_min') }}
              </span>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Maximum ($)" label-for="job-pay-max">
              <b-form-input
                id="job-pay-max"
                v-model="form.pay_max"
                type="number"
                min="0"
                step="0.01"
                :state="form.$hasError('pay_max') ? false : null"
              />
              <span
                v-if="form.$hasError('pay_max')"
                class="input-error d-block"
              >
                {{ errorText('pay_max') }}
              </span>
            </b-form-group>
          </b-col>
        </template>
      </b-row>
      <b-form-checkbox v-if="form.pay_type" v-model="form.show_pay">
        Show pay on the job advertisement
      </b-form-checkbox>
    </job-form-section>

    <job-form-section
      title="Location"
      description="Where the work is based. For fly-in fly-out roles, also add the departure point and the site location."
    >
      <b-row>
        <b-col md="6">
          <b-form-group label="Suburb" label-for="job-suburb">
            <b-form-input
              id="job-suburb"
              v-model="form.suburb"
              maxlength="100"
              :state="form.$hasError('suburb') ? false : null"
            />
            <span v-if="form.$hasError('suburb')" class="input-error d-block">
              {{ errorText('suburb') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="State" label-for="job-state">
            <b-form-select
              id="job-state"
              v-model="form.state"
              :options="stateOptions"
              :state="form.$hasError('state') ? false : null"
            />
            <span v-if="form.$hasError('state')" class="input-error d-block">
              {{ errorText('state') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Postcode" label-for="job-postcode">
            <b-form-input
              id="job-postcode"
              v-model="form.postcode"
              inputmode="numeric"
              maxlength="4"
              :state="form.$hasError('postcode') ? false : null"
            />
            <span v-if="form.$hasError('postcode')" class="input-error d-block">
              {{ errorText('postcode') }}
            </span>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-checkbox v-model="form.is_fifo">
        This is a FIFO role
      </b-form-checkbox>
      <b-row v-if="form.is_fifo" class="mt-3">
        <b-col md="6">
          <b-form-group label="FIFO point location*" label-for="job-fifo-point">
            <b-form-input
              id="job-fifo-point"
              v-model="form.fifo_point_location"
              maxlength="255"
              placeholder="e.g. Perth Airport"
              :state="form.$hasError('fifo_point_location') ? false : null"
            />
            <span
              v-if="form.$hasError('fifo_point_location')"
              class="input-error d-block"
            >
              {{ errorText('fifo_point_location') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Site location*" label-for="job-site-location">
            <b-form-input
              id="job-site-location"
              v-model="form.site_location"
              maxlength="255"
              placeholder="e.g. Newman mine site"
              :state="form.$hasError('site_location') ? false : null"
            />
            <span
              v-if="form.$hasError('site_location')"
              class="input-error d-block"
            >
              {{ errorText('site_location') }}
            </span>
          </b-form-group>
        </b-col>
      </b-row>
    </job-form-section>

    <job-form-section
      title="Role details"
      description="The roster, experience level, start date and number of positions, plus any accommodation or travel you provide."
    >
      <b-row>
        <b-col md="6">
          <b-form-group label="Roster" label-for="job-roster">
            <b-form-select
              id="job-roster"
              v-model="form.roster"
              :options="rosterOptions"
              :state="form.$hasError('roster') ? false : null"
            />
            <span v-if="form.$hasError('roster')" class="input-error d-block">
              {{ errorText('roster') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Experience level" label-for="job-experience">
            <b-form-select
              id="job-experience"
              v-model="form.experience_level"
              :options="experienceOptions"
              :state="form.$hasError('experience_level') ? false : null"
            />
            <span
              v-if="form.$hasError('experience_level')"
              class="input-error d-block"
            >
              {{ errorText('experience_level') }}
            </span>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group
            label="Start date"
            label-for="job-start-date"
            description="Optional. When the work is due to start."
          >
            <b-form-datepicker
              id="job-start-date"
              v-model="form.start_date"
              locale="en-AU"
              today-button
              reset-button
              no-flip
              placeholder="dd/mm/yyyy"
              :min="minDate"
              :date-format-options="dateFormatOptions"
            />
            <span
              v-if="form.$hasError('start_date')"
              class="input-error d-block"
            >
              {{ errorText('start_date') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Number of positions"
            label-for="job-positions"
            description="How many people you are hiring for with this job."
          >
            <b-form-input
              id="job-positions"
              v-model="form.positions"
              type="number"
              min="1"
              max="999"
              :state="form.$hasError('positions') ? false : null"
            />
            <span
              v-if="form.$hasError('positions')"
              class="input-error d-block"
            >
              {{ errorText('positions') }}
            </span>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="What you provide" class="mb-0">
        <b-form-checkbox v-model="form.accommodation_provided">
          Accommodation provided
        </b-form-checkbox>
        <b-form-checkbox v-model="form.travel_provided" class="mt-2">
          Travel provided
        </b-form-checkbox>
      </b-form-group>
    </job-form-section>

    <job-form-section
      title="Tickets required"
      description="Select the tickets and licences applicants need. Each applicant is shown against this list so you can see who is qualified. Add any ticket that is not listed."
    >
      <div class="ticket-chips">
        <button
          v-for="ticket in ticketChoices"
          :key="ticket"
          type="button"
          class="ticket-chip"
          :class="{ selected: isTicketSelected(ticket) }"
          :aria-pressed="isTicketSelected(ticket) ? 'true' : 'false'"
          @click="toggleTicket(ticket)"
        >
          {{ ticket }}
        </button>
        <button
          type="button"
          class="ticket-chip ticket-chip-other"
          :class="{ selected: showOtherTicket }"
          :aria-pressed="showOtherTicket ? 'true' : 'false'"
          @click="showOtherTicket = !showOtherTicket"
        >
          + Other
        </button>
      </div>
      <div
        v-if="showOtherTicket"
        class="ticket-other d-flex align-items-center mt-3"
      >
        <b-form-input
          v-model="customTicket"
          class="flex-grow-1 mr-3"
          maxlength="100"
          autocomplete="off"
          placeholder="Other ticket, e.g. Forklift licence"
          aria-label="Other ticket"
          @keydown.enter.prevent="addCustomTicket"
        />
        <b-button
          variant="primary"
          class="ticket-add-btn flex-shrink-0 px-4"
          style="height: 50px; min-width: 100px"
          :disabled="!customTicket.trim()"
          @click="addCustomTicket"
        >
          ADD
        </b-button>
      </div>
      <span
        v-if="form.$hasError('required_tickets')"
        class="input-error d-block"
      >
        {{ errorText('required_tickets') }}
      </span>
    </job-form-section>

    <job-form-section
      title="Visibility and links"
      description="Choose who can see this job, optionally link it to one of your projects, and add your company website for applicants to visit."
    >
      <b-row>
        <b-col md="6">
          <b-form-group
            label="Who can see this job"
            label-for="job-visibility"
            description="Internal jobs are only shown to tradies already in your workforce."
          >
            <b-form-select
              id="job-visibility"
              v-model="form.visibility"
              :options="visibilityOptions"
              :state="form.$hasError('visibility') ? false : null"
            />
            <span
              v-if="form.$hasError('visibility')"
              class="input-error d-block"
            >
              {{ errorText('visibility') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Linked project"
            label-for="job-project"
            description="Optional. The job will also show on that project's Jobs tab."
          >
            <b-form-select
              id="job-project"
              v-model="form.project_id"
              :options="projectOptions"
              :state="form.$hasError('project_id') ? false : null"
              @change="onProjectChosen"
            />
            <span
              v-if="form.$hasError('project_id')"
              class="input-error d-block"
            >
              {{ errorText('project_id') }}
            </span>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group
        label="Company website"
        label-for="job-company-website"
        description="Optional. Shown to tradies as a link to your company page."
        class="mb-0"
      >
        <b-form-input
          id="job-company-website"
          v-model="form.company_website"
          type="url"
          maxlength="255"
          placeholder="https://example.com.au"
          :state="form.$hasError('company_website') ? false : null"
        />
        <span
          v-if="form.$hasError('company_website')"
          class="input-error d-block"
        >
          {{ errorText('company_website') }}
        </span>
      </b-form-group>
    </job-form-section>

    <job-form-section
      title="Posting dates"
      description="When the job goes live and when it stops accepting applications."
    >
      <b-row>
        <b-col md="6">
          <b-form-group
            label="Live from"
            label-for="job-live-from"
            class="mb-3 mb-md-0"
          >
            <b-form-datepicker
              id="job-live-from"
              v-model="form.live_from"
              locale="en-AU"
              today-button
              reset-button
              no-flip
              placeholder="dd/mm/yyyy"
              :min="minDate"
              :max="maxLiveFromDate"
              :disabled="isLive"
              :date-format-options="dateFormatOptions"
              @input="onLiveFromChanged"
            />
            <span
              v-if="form.$hasError('live_from')"
              class="input-error d-block"
            >
              {{ errorText('live_from') }}
            </span>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Expires on"
            label-for="job-expires-at"
            class="mb-0"
          >
            <b-form-datepicker
              id="job-expires-at"
              v-model="form.expires_at"
              locale="en-AU"
              today-button
              reset-button
              no-flip
              placeholder="dd/mm/yyyy"
              :min="expiryMin"
              :max="expiryMax"
              :date-format-options="dateFormatOptions"
              @input="onExpiresChanged"
            />
            <span
              v-if="form.$hasError('expires_at')"
              class="input-error d-block"
            >
              {{ errorText('expires_at') }}
            </span>
          </b-form-group>
        </b-col>
      </b-row>
    </job-form-section>

    <div class="d-flex flex-wrap mt-4 job-form-actions">
      <b-button
        variant="secondary"
        type="button"
        :disabled="saving"
        @click="$emit('cancel')"
      >
        CANCEL
      </b-button>
      <b-button
        :variant="canPublishNow ? 'light' : 'primary'"
        type="submit"
        class="save-btn"
        :disabled="saving"
      >
        {{ saveLabel }}
      </b-button>
      <b-button
        v-if="canPublishNow"
        variant="primary"
        type="button"
        class="publish-btn"
        :disabled="saving"
        @click="onSave(true)"
      >
        SAVE &amp; PUBLISH
      </b-button>
    </div>
  </b-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import apiToasts from '@/mixins/apiToasts'
import Form from '@/utils/form'
import JobFormSection from '@/components/jobPostings/JobFormSection.vue'
import {
  AU_STATES,
  EMPLOYMENT_TYPES,
  EXPERIENCE_LEVELS,
  MAX_LIVE_DAYS,
  MAX_SCHEDULE_DAYS,
  PAY_TYPES,
  ROSTER_OPTIONS,
  VISIBILITY_OPTIONS,
  addDaysISO,
  expiryAfterLiveFromChange,
  liveFromAfterExpiryChange,
  maxExpiryFor,
  maxLiveFromISO,
  maxReachISO,
  parseAddressLocation,
  todayISO,
} from '@/utils/jobPostings'

// Optional fields that can be cleared on edit (sent as null only when they had
// a value before and are now empty — never for fields that were never set).
const OPTIONAL_FIELDS = [
  'live_from',
  'trade_id',
  'project_id',
  'roster',
  'experience_level',
  'start_date',
  'company_website',
  'pay_type',
  'pay_min',
  'pay_max',
  'suburb',
  'state',
  'postcode',
  'expires_at',
]

// Template labels that are identity documents, not tickets a job would ask for
// (same exclusions as the candidate search ticket filter).
const NON_TICKET_LABELS = [
  'Passport',
  'Birth Certificate',
  'Australian Citizenship',
  'VISA',
  'Proof of Age',
]

function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === ''
}

export default {
  name: 'JobPostingForm',

  components: { JobFormSection },

  mixins: [apiToasts],

  props: {
    // Existing job when editing; null when creating.
    job: {
      type: Object,
      default: null,
    },
    // A closed job to copy from; its details prefill a new job.
    template: {
      type: Object,
      default: null,
    },
  },

  data() {
    const job = this.job || this.template || {}
    const pay = job.pay || {}
    const location = job.location || {}
    return {
      saving: false,
      employmentTypeOptions: [
        { value: null, text: 'Select...' },
        ...EMPLOYMENT_TYPES,
      ],
      payTypeOptions: [{ value: null, text: 'Not specified' }, ...PAY_TYPES],
      stateOptions: [{ value: null, text: 'Select...' }, ...AU_STATES],
      visibilityOptions: VISIBILITY_OPTIONS,
      rosterOptions: [
        { value: null, text: 'Not specified' },
        ...ROSTER_OPTIONS,
      ],
      experienceOptions: [
        { value: null, text: 'Not specified' },
        ...EXPERIENCE_LEVELS,
      ],
      // Ticket names from the templates list; the form stays usable if it fails.
      ticketOptions: [],
      // Custom tickets this company has used on its earlier jobs.
      companyTickets: [],
      customTicket: '',
      showOtherTicket: false,
      // Loaded on create; the project picker stays usable (just empty) if it fails.
      projects: [],
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      minDate: todayISO(),
      form: new Form({
        title: job.title || '',
        description: job.description || '',
        employment_type: job.employment_type || null,
        trade_id: job.trade_id || null,
        // "Post a job" from a project's Jobs tab arrives with ?project_id=.
        project_id:
          job.project_id ||
          (!this.job && this.$route && Number(this.$route.query.project_id)) ||
          null,
        visibility: job.visibility || 'external',
        is_fifo: !!job.is_fifo,
        fifo_point_location: job.fifo_point_location || '',
        site_location: job.site_location || '',
        roster: job.roster || null,
        experience_level: job.experience_level || null,
        start_date: job.start_date || '',
        positions: job.positions || 1,
        accommodation_provided: !!job.accommodation_provided,
        travel_provided: !!job.travel_provided,
        required_tickets: [...(job.required_tickets || [])],
        company_website: job.company_website || '',
        pay_type: pay.type || null,
        pay_min: isBlank(pay.min) ? '' : pay.min,
        pay_max: isBlank(pay.max) ? '' : pay.max,
        show_pay: pay.show !== undefined ? !!pay.show : true,
        suburb: location.suburb || '',
        state: location.state || null,
        postcode: location.postcode || '',
        // The datepicker works in YYYY-MM-DD; the time part is re-added on send.
        // A new job starts today and runs the full 30 days; the user can shorten it.
        expires_at: this.job
          ? job.expires_at
            ? String(job.expires_at).slice(0, 10)
            : ''
          : maxExpiryFor(todayISO()),
        live_from: this.job ? this.initialLiveFrom(job) : todayISO(),
      }),
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    isEdit() {
      return !!(this.job && this.job.id)
    },

    // Published and already past its go-live date: it cannot be rescheduled, and
    // its expiry can only be extended to 30 days from today.
    isLive() {
      return (
        this.isEdit &&
        this.job.status === 'published' &&
        !this.job.is_scheduled &&
        !this.job.is_expired
      )
    },

    maxLiveFromDate() {
      return maxLiveFromISO()
    },

    expiryMin() {
      const goLive = this.form.live_from
      return !this.isLive && !isBlank(goLive) && goLive > this.minDate
        ? addDaysISO(goLive, 1)
        : this.minDate
    },

    // With a go-live date chosen the expiry stays within 30 days of it; with
    // none, picking one far out sets the go-live date for them.
    expiryMax() {
      if (this.isLive) return maxExpiryFor('')
      return isBlank(this.form.live_from)
        ? maxReachISO()
        : maxExpiryFor(this.form.live_from)
    },

    showPayAmounts() {
      return !!this.form.pay_type && this.form.pay_type !== 'negotiable'
    },

    // "Save & publish" is only offered when the API would allow it (publishing
    // needs jobs.publish). On edit it only applies to drafts.
    canPublishNow() {
      if (!this.hasPermission('jobs.publish')) return false
      return !this.isEdit || this.job.status === 'draft'
    },

    saveLabel() {
      if (this.isEdit) return 'SAVE CHANGES'
      return this.canPublishNow ? 'SAVE AS DRAFT' : 'SAVE'
    },

    projectOptions() {
      const projects = this.projects.map((p) => ({ value: p.id, text: p.name }))
      // An edited job may be linked to a project that is now archived.
      const linked = this.job && this.job.project
      if (linked && !projects.some((p) => p.value === linked.id)) {
        projects.unshift({ value: linked.id, text: linked.name })
      }
      return [{ value: null, text: 'No linked project' }, ...projects]
    },

    // A ticket saved on the job but no longer in the templates list stays
    // tickable, so editing never silently drops it.
    ticketChoices() {
      // Listed tickets first so their spelling wins over a case-variant of the
      // company's own.
      const seen = new Set()
      return [
        ...this.ticketOptions,
        ...this.companyTickets,
        ...this.form.required_tickets,
      ]
        .filter((t) => {
          const key = t.toLowerCase()
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        .sort((a, b) => a.localeCompare(b))
    },

    tradeOptions() {
      const trades = (this.$store.state.trade.trades || [])
        .slice()
        .sort((a, b) => (a.name < b.name ? -1 : 1))
        .map((t) => ({ value: t.id, text: t.name }))
      return [{ value: null, text: 'Select a trade' }, ...trades]
    },
  },

  async created() {
    if (!this.isEdit) this.prefillFromCompany()
    this.fetchProjectOptions()
      .then((projects) => {
        this.projects = projects
      })
      .catch(() => {})
    this.fetchTicketOptions()
    this.fetchCompanyTickets()
    try {
      await this.getPickerValues()
    } catch (err) {
      // Trade is optional — the form stays usable without the list.
      this.showToast(
        'Error',
        'Could not load trades. Please try again.',
        'danger',
      )
    }
  },

  methods: {
    // A saved go-live date that has passed means "live now": show it blank,
    // except on a live job, where it is shown (read only).
    initialLiveFrom(job) {
      if (!job || !job.live_from) return ''
      const date = String(job.live_from).slice(0, 10)
      const live =
        job.status === 'published' && !job.is_scheduled && !job.is_expired
      return live || date > todayISO() ? date : ''
    },

    // The two pickers keep each other in step: a go-live date fills or trims
    // the expiry to a 30 day window, and an expiry sets the go-live date 30
    // days before it, never before today.
    onLiveFromChanged() {
      if (this.isLive) return
      this.form.expires_at = expiryAfterLiveFromChange({
        liveFrom: this.form.live_from,
        expiresAt: this.form.expires_at,
      })
    },

    onExpiresChanged() {
      if (this.isLive) return
      this.form.live_from = liveFromAfterExpiryChange({
        liveFrom: this.form.live_from,
        expiresAt: this.form.expires_at,
      })
    },

    ...mapActions('trade', ['getPickerValues']),
    ...mapActions('company', ['getCompany']),
    ...mapActions('jobPosting', [
      'createJob',
      'updateJob',
      'publishJob',
      'fetchProjectOptions',
    ]),

    // Adds a ticket the list doesn't have. A name that matches a listed or
    // already-added ticket (ignoring case) just ticks that one, so "white card"
    // never becomes a second "White Card".
    isTicketSelected(ticket) {
      return this.form.required_tickets.includes(ticket)
    },

    toggleTicket(ticket) {
      const tickets = this.form.required_tickets
      this.form.required_tickets = tickets.includes(ticket)
        ? tickets.filter((t) => t !== ticket)
        : [...tickets, ticket]
    },

    addCustomTicket() {
      const name = this.customTicket.trim().replace(/\s+/g, ' ')
      if (!name) return
      const existing = this.ticketChoices.find(
        (t) => t.toLowerCase() === name.toLowerCase(),
      )
      const ticket = existing || name
      if (!this.form.required_tickets.includes(ticket)) {
        this.form.required_tickets.push(ticket)
      }
      this.customTicket = ''
    },

    // Custom tickets from this company's own earlier jobs, so a ticket added
    // once is offered again on the next post (the API scopes it per company).
    async fetchCompanyTickets() {
      try {
        const { data } = await this.$api.get('job-postings/tickets')
        this.companyTickets = (data.data || []).filter(
          (t) => typeof t === 'string' && t.trim(),
        )
      } catch (err) {
        // Suggestions only — the form works without them.
      }
    },

    async fetchTicketOptions() {
      try {
        const { data } = await this.$api.get('templates/getTemplateOptions')
        this.ticketOptions = (data.data || [])
          .map((t) => t.label)
          .filter((label) => label && !NON_TICKET_LABELS.includes(label))
      } catch (err) {
        // Tickets are optional — leave the list empty rather than block the form.
      }
    },

    // The project gives the job its location, so an empty address is filled in
    // from it; anything the user already typed is left alone.
    onProjectChosen(projectId) {
      const project = this.projects.find((p) => p.id === projectId)
      if (!project || !project.address) return
      const location = parseAddressLocation(project.address)
      if (!location) return
      const f = this.form
      if (isBlank(f.suburb)) f.suburb = location.suburb
      if (isBlank(f.state)) f.state = location.state
      if (isBlank(f.postcode) && location.postcode) {
        f.postcode = location.postcode
      }
    },

    // New posts start with the company's own suburb / state / postcode, since
    // most jobs are local to it. Best effort: it never overwrites what the user
    // has already typed and any failure just leaves the fields blank.
    async prefillFromCompany() {
      const user = this.$store.state.auth.user
      if (!user || !user.company_id) return
      try {
        await this.getCompany(user.company_id)
      } catch (err) {
        return
      }
      const company = this.$store.state.company.company
      const location = parseAddressLocation(company && company.address)
      if (!location) return
      const f = this.form
      if (isBlank(f.suburb)) f.suburb = location.suburb
      if (isBlank(f.state)) f.state = location.state
      if (isBlank(f.postcode) && location.postcode) {
        f.postcode = location.postcode
      }
    },

    errorText(field) {
      const error = this.form.$getError(field)
      return Array.isArray(error) ? error[0] : error
    },

    // Client-side checks mirror the API rules so obvious mistakes never
    // round-trip. Returns a Laravel-shaped errors object ({ field: [msg] }).
    validate() {
      const f = this.form
      const errors = {}
      if (isBlank(f.title)) errors.title = ['Enter a job title.']
      if (isBlank(f.description)) errors.description = ['Enter a description.']
      if (isBlank(f.employment_type)) {
        errors.employment_type = ['Select an employment type.']
      }
      if (f.is_fifo && isBlank(f.fifo_point_location)) {
        errors.fifo_point_location = ['Enter the FIFO point location.']
      }
      if (f.is_fifo && isBlank(f.site_location)) {
        errors.site_location = ['Enter the site location.']
      }
      const min = isBlank(f.pay_min) ? null : Number(f.pay_min)
      const max = isBlank(f.pay_max) ? null : Number(f.pay_max)
      if (min !== null && min < 0) errors.pay_min = ['Must be 0 or more.']
      if (max !== null && max < 0) errors.pay_max = ['Must be 0 or more.']
      if (min !== null && max !== null && max < min && !errors.pay_max) {
        errors.pay_max = ['Maximum pay must be at least the minimum.']
      }
      if (!isBlank(f.postcode) && !/^\d{4}$/.test(String(f.postcode).trim())) {
        errors.postcode = ['Postcode must be exactly 4 digits.']
      }
      if (
        !isBlank(f.company_website) &&
        !/^https?:\/\/\S+$/i.test(f.company_website.trim())
      ) {
        errors.company_website = [
          'Enter a full web address starting with http:// or https://',
        ]
      }
      const positions = Number(f.positions)
      if (!Number.isInteger(positions) || positions < 1 || positions > 999) {
        errors.positions = ['Enter a whole number from 1 to 999.']
      }
      const goLive = this.isLive ? '' : f.live_from
      if (!isBlank(goLive)) {
        if (goLive < this.minDate) {
          errors.live_from = ['Live from date must be today or later.']
        } else if (goLive > this.maxLiveFromDate) {
          errors.live_from = [
            `A job can be scheduled at most ${MAX_SCHEDULE_DAYS} days ahead.`,
          ]
        }
      }
      if (!isBlank(f.expires_at)) {
        if (f.expires_at < this.minDate) {
          errors.expires_at = ['Expiry date must be today or later.']
        } else if (
          !isBlank(goLive) &&
          goLive > this.minDate &&
          f.expires_at <= goLive
        ) {
          errors.expires_at = [
            'Expiry date must be after the date the job goes live.',
          ]
        } else if (f.expires_at > maxExpiryFor(goLive)) {
          errors.expires_at = [
            `A job can be live for at most ${MAX_LIVE_DAYS} days. Choose a date within ${MAX_LIVE_DAYS} days of when it goes live.`,
          ]
        }
      }
      return errors
    },

    buildPayload(publish) {
      const f = this.form
      const payload = {
        title: f.title.trim(),
        description: f.description.trim(),
        employment_type: f.employment_type,
        visibility: f.visibility,
        is_fifo: !!f.is_fifo,
      }
      // A non-FIFO job has no fly-in point or site (the API clears them too).
      if (f.is_fifo) {
        payload.fifo_point_location = f.fifo_point_location.trim()
        payload.site_location = f.site_location.trim()
      }
      const original = this.isEdit ? this.originalValues() : {}

      const values = {
        trade_id: f.trade_id,
        project_id: f.project_id,
        roster: f.roster,
        experience_level: f.experience_level,
        start_date: isBlank(f.start_date) ? null : f.start_date,
        company_website: isBlank(f.company_website)
          ? null
          : f.company_website.trim(),
        pay_type: f.pay_type,
        pay_min: isBlank(f.pay_min) ? null : Number(f.pay_min),
        pay_max: isBlank(f.pay_max) ? null : Number(f.pay_max),
        suburb: isBlank(f.suburb) ? null : f.suburb.trim(),
        state: f.state,
        postcode: isBlank(f.postcode) ? null : String(f.postcode).trim(),
        expires_at: isBlank(f.expires_at) ? null : `${f.expires_at} 23:59:59`,
        // Today or earlier means live as soon as it is published.
        live_from:
          isBlank(f.live_from) || f.live_from <= this.minDate
            ? null
            : `${f.live_from} 00:00:00`,
      }
      // A live job cannot be rescheduled: leave its go-live date alone.
      if (this.isLive) values.live_from = undefined
      // Negotiable has no amounts to send.
      if (values.pay_type === 'negotiable' || !values.pay_type) {
        values.pay_min = null
        values.pay_max = null
      }
      OPTIONAL_FIELDS.forEach((key) => {
        if (values[key] === undefined) return
        if (values[key] !== null) payload[key] = values[key]
        else if (!isBlank(original[key])) payload[key] = null
      })

      // Positions default to 1: only sent when it is not 1, or when an edit
      // changes it back.
      const positions = Number(f.positions)
      if (positions !== 1 || (original.positions || 1) !== 1) {
        payload.positions = positions
      }
      // Perks and tickets: sent when set, or when an edit clears them.
      ;['accommodation_provided', 'travel_provided'].forEach((key) => {
        if (f[key] || original[key]) payload[key] = !!f[key]
      })
      if (
        f.required_tickets.length ||
        (original.required_tickets || []).length
      ) {
        payload.required_tickets = f.required_tickets
      }

      if (values.pay_type) {
        payload.pay_currency = 'AUD'
        payload.show_pay = !!f.show_pay
      }
      // New posts go live at once only when the user chose Save & publish.
      if (!this.isEdit && publish) payload.status = 'published'
      return payload
    },

    // Previous values of the clearable fields, in the same shape as payload.
    originalValues() {
      const pay = this.job.pay || {}
      const location = this.job.location || {}
      return {
        trade_id: this.job.trade_id,
        project_id: this.job.project_id,
        roster: this.job.roster,
        experience_level: this.job.experience_level,
        start_date: this.job.start_date,
        positions: this.job.positions,
        company_website: this.job.company_website,
        accommodation_provided: this.job.accommodation_provided,
        travel_provided: this.job.travel_provided,
        required_tickets: this.job.required_tickets,
        pay_type: pay.type,
        pay_min: pay.min,
        pay_max: pay.max,
        suburb: location.suburb,
        state: location.state,
        postcode: location.postcode,
        expires_at: this.job.expires_at,
        live_from: this.job.live_from,
      }
    },

    async onSave(publish) {
      if (this.saving) return
      this.form.$clearErrors()
      this.form.$setErrorMessage('')
      const errors = this.validate()
      if (Object.keys(errors).length) {
        this.form.$setErrors(errors)
        return
      }

      this.saving = true
      try {
        const payload = this.buildPayload(publish)
        let saved
        if (this.isEdit) {
          saved = await this.updateJob({ id: this.job.id, payload })
          if (publish) saved = await this.publishJob(this.job.id)
        } else {
          saved = await this.createJob(payload)
        }
        this.$emit('saved', saved)
      } catch (err) {
        // Store actions reject with the axios response; $handleError wants the
        // error wrapping it (422 -> per-field errors, else a generic message).
        this.form.$handleError({ response: err })
        // No response at all (network failure) — $handleError ignores it.
        if (!err) {
          this.form.$setErrorMessage('Something went wrong. Please try again.')
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// `.btn` is a fixed 56px height globally; the actions just need spacing.
.job-form-actions {
  gap: 16px;

  .btn {
    min-width: 140px;
  }

  // Save as draft: dark slate (DESIGN.md gray-700) with white text, so it has
  // strong contrast on the pale page and is distinct from the grey Cancel and
  // the blue Save & publish.
  .save-btn.btn-light {
    background-color: #374151;
    border-color: #374151;
    color: #fff;

    &:hover,
    &:focus {
      background-color: #1f2937;
      border-color: #1f2937;
      color: #fff;
    }
  }
}

// Long ticket list: scroll inside the box instead of stretching the page.
// Tickets as tap-to-select pills; every ticket shows at once and wraps.
.ticket-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ticket-chip {
  padding: 6px 16px;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;

  &:hover {
    border-color: #2c66d5;
  }

  &:focus {
    outline: none;
    border-color: #2c66d5;
  }

  &.selected {
    color: #006cff;
    font-weight: 600;
    background: #eaf2ff;
    border-color: #006cff;
  }
}

// Input and ADD sit on one row at the same 50px height (`.btn` is 56px
// globally, so it is pinned to the input's height here).
.ticket-other {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;

  .form-control {
    flex: 1;
  }

  .ticket-add-btn {
    flex: none;
    height: 50px;
    min-width: 100px;
    padding: 0 24px;
  }
}

.ticket-chip-other {
  border-style: dashed;
  color: #6b7280;

  &.selected {
    border-style: solid;
  }
}
</style>
